/**
 * MerchantDrillDownDialog.jsx — opens from a P&L row click.
 *
 * Three sections: charge waterfall (per-type breakdown with bar widths
 * proportional to amount), bin occupancy (top 50 occupied locations), and
 * recent invoices (last 10 with status badges).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { get3plMerchantDrillDown } from '../../lib/api';
import { fmt$, fmtDate } from './utils';

const TYPE_COLOR = {
  storage:    'bg-sky-500',
  inbound:    'bg-emerald-500',
  outbound:   'bg-violet-500',
  vas:        'bg-amber-500',
  minimum:    'bg-rose-500',
  adjustment: 'bg-slate-500',
  credit:     'bg-emerald-700',
  unknown:    'bg-slate-300',
};

export default function MerchantDrillDownDialog({ open, onOpenChange, merchant }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !merchant) {
      setData(null);
      return;
    }
    setLoading(true);
    get3plMerchantDrillDown(merchant.merchant_id)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load drill-down'))
      .finally(() => setLoading(false));
  }, [open, merchant]);

  // Compute the max amount across types so the waterfall bars share a scale.
  const breakdown = data?.charge_breakdown || [];
  const maxAmount = breakdown.reduce((m, b) => Math.max(m, b.total), 0) || 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="merchant-drilldown-dialog">
        <DialogHeader>
          <DialogTitle>{merchant?.name || 'Merchant'} · drill-down</DialogTitle>
          <DialogDescription>
            Last {data?.lookback_days || 30} days · {merchant?.is_default ? 'Default merchant (absorbs un-tagged inventory)' : 'Named merchant'}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="p-6 text-center text-muted-foreground">Loading…</div>}

        {!loading && data && (
          <div className="space-y-6 pt-2">
            {/* Pallet hire YTD tile — surfaces pooled-pallet revenue alongside
                storage/inbound/outbound so operators don't miss the silent
                income stream from CHEP/Loscam holdings. Only shown when the
                merchant has accrued pallet hire in the lookback OR YTD. */}
            {data.pallet_hire && (data.pallet_hire.ytd_total > 0 || data.pallet_hire.lookback_total > 0) && (
              <section className="grid grid-cols-2 gap-3" data-testid="drilldown-pallet-hire">
                <div className="border border-violet-200 bg-violet-50 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">
                    Pallet hire · last {data.pallet_hire.lookback_days}d
                  </div>
                  <div className="text-2xl font-bold font-mono text-violet-900 mt-1">
                    {fmt$(data.pallet_hire.lookback_total)}
                  </div>
                  <div className="text-[11px] text-violet-700 mt-0.5">
                    {data.pallet_hire.lookback_count} accrual{data.pallet_hire.lookback_count === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                    Pallet hire · YTD ({data.pallet_hire.ytd_year})
                  </div>
                  <div className="text-2xl font-bold font-mono text-emerald-900 mt-1">
                    {fmt$(data.pallet_hire.ytd_total)}
                  </div>
                  <div className="text-[11px] text-emerald-700 mt-0.5">
                    {data.pallet_hire.ytd_count} accrual{data.pallet_hire.ytd_count === 1 ? '' : 's'} since Jan 1
                  </div>
                </div>
              </section>
            )}

            {/* Section 1: Charge waterfall */}
            <section data-testid="drilldown-waterfall">
              <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">
                Charge breakdown · {fmt$(data.breakdown_total)} total
              </h3>
              {breakdown.length === 0 && (
                <div className="text-sm text-muted-foreground p-4 border border-dashed border-border bg-muted/30">
                  No charges in the last {data.lookback_days} days.
                </div>
              )}
              {breakdown.length > 0 && (
                <div className="space-y-1.5">
                  {breakdown.map((b) => {
                    const pct = (b.total / maxAmount) * 100;
                    const color = TYPE_COLOR[b.type] || TYPE_COLOR.unknown;
                    return (
                      <div key={b.type} className="flex items-center gap-2 text-sm" data-testid={`waterfall-row-${b.type}`}>
                        <div className="w-24 shrink-0 text-xs uppercase tracking-wider font-semibold">
                          {b.type}
                        </div>
                        <div className="flex-1 relative bg-muted h-6 overflow-hidden">
                          <div className={`absolute left-0 top-0 h-full ${color}`} style={{ width: `${pct}%` }} />
                          <div className="absolute inset-0 flex items-center px-2 font-mono text-xs font-bold text-foreground mix-blend-difference">
                            {fmt$(b.total)} · {b.count} line{b.count === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Section 2: Bin occupancy */}
            <section data-testid="drilldown-bins">
              <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">
                Bin occupancy · top {Math.min(data.bin_count, 50)}
              </h3>
              {data.bin_count === 0 && (
                <div className="text-sm text-muted-foreground p-4 border border-dashed border-border bg-muted/30">
                  No occupied bins for this merchant.
                </div>
              )}
              {data.bin_count > 0 && (
                <div className="border border-border bg-card max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 border-b border-border sticky top-0">
                      <tr className="text-left">
                        <th className="p-2">Location</th>
                        <th className="p-2">Zone</th>
                        <th className="p-2 text-right">Qty</th>
                        <th className="p-2">Tagged?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.bin_occupancy.map((b, idx) => (
                        <tr key={idx} className="border-b border-border last:border-b-0">
                          <td className="p-2 font-mono">{b.location_code}</td>
                          <td className="p-2">{b.zone || '—'}</td>
                          <td className="p-2 text-right font-mono">{b.quantity}</td>
                          <td className="p-2">
                            {b.merchant_tagged
                              ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800">tagged</span>
                              : <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800">untagged</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Section 3: Recent invoices */}
            <section data-testid="drilldown-invoices">
              <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">
                Recent invoices
              </h3>
              {data.recent_invoices.length === 0 && (
                <div className="text-sm text-muted-foreground p-4 border border-dashed border-border bg-muted/30">
                  No invoices generated yet for this merchant.
                </div>
              )}
              {data.recent_invoices.length > 0 && (
                <div className="border border-border bg-card overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 border-b border-border">
                      <tr className="text-left">
                        <th className="p-2">Number</th>
                        <th className="p-2">Period</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-border last:border-b-0">
                          <td className="p-2 font-mono font-bold">{inv.invoice_number}</td>
                          <td className="p-2">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                          <td className="p-2 text-right font-mono font-bold">{fmt$(inv.total)}</td>
                          <td className="p-2">
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${
                              inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                              inv.status === 'sent' ? 'bg-sky-100 text-sky-800' :
                              (inv.status === 'voided' || inv.status === 'void') ? 'bg-slate-200 text-slate-600' :
                              'bg-amber-100 text-amber-800'
                            }`}>
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
